import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bot, Menu, User, LogOut, Home } from 'lucide-react';
import { useAuth } from '../../lib/AuthContext';

interface HeaderProps {
  onMenuClick?: () => void;
}

export function Header({ onMenuClick }: HeaderProps) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <header className="bg-white/95 backdrop-blur-xl border-b border-dark-200/30 sticky top-0 z-30 shadow-soft">
      <div className="px-4 md:px-6 py-3 md:py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-4">
            {onMenuClick && (
              <button
                onClick={onMenuClick}
                className="lg:hidden p-2 text-dark-600 hover:text-dark-900 hover:bg-dark-50 rounded-lg transition-all duration-200"
                aria-label="Open menu"
              >
                <Menu className="w-6 h-6" />
              </button>
            )}
            
            <Link to="/" className="flex items-center gap-2 md:gap-3">
              <div className="p-2 md:p-3 bg-gradient-to-br from-primary-600 to-accent-600 rounded-xl shadow-medium">
                <Bot className="w-6 h-6 md:w-8 md:h-8 text-white" />
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-primary-600 to-accent-600 bg-clip-text text-transparent">
                  Agent Store
                </h1>
                <p className="text-xs md:text-sm text-dark-600">Discover AI agents from across the web</p>
              </div>
            </Link>
          </div>

          <div className="flex items-center gap-2 md:gap-4">
            {/* Home link - always visible */}
            <Link 
              to="/" 
              className="hidden md:flex items-center gap-2 px-4 py-2 text-dark-700 hover:text-dark-900 hover:bg-dark-50 rounded-lg transition-all duration-200"
            >
              <Home className="w-5 h-5 text-dark-600" />
              <span>Home</span>
            </Link>

            {user ? (
              <div className="relative">
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="flex items-center gap-2 px-3 py-2 border-2 border-dark-200 rounded-xl hover:bg-dark-50 hover:border-primary-300 transition-all duration-200"
                >
                  <User className="w-5 h-5 text-dark-600" />
                  <span className="text-dark-700 hidden sm:inline font-medium">{user.email?.split('@')[0]}</span>
                </button>
                
                {menuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-strong py-2 z-10 border border-dark-200">
                    <Link
                      to="/profile"
                      className="block px-4 py-3 text-dark-700 hover:bg-dark-50 transition-colors"
                      onClick={() => setMenuOpen(false)}
                    >
                      Profile
                    </Link>
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        handleSignOut();
                      }}
                      className="block w-full text-left px-4 py-3 text-dark-700 hover:bg-dark-50 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <LogOut className="w-4 h-4" />
                        Sign out
                      </div>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2 md:gap-3">
                <Link
                  to="/login"
                  className="px-4 py-2 text-dark-700 hover:text-dark-900 hover:bg-dark-50 rounded-lg transition-all duration-200 text-sm md:text-base font-medium"
                >
                  Sign in
                </Link>
                <Link
                  to="/signup"
                  className="btn-primary text-sm md:text-base"
                >
                  Sign up
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}